import { ReactNode } from 'react';
import { Store, NOTIFICATION_TYPE } from 'react-notifications-component';
import tw from 'twin.macro';
import 'animate.css/animate.css';

import { CircleCheckIcon } from '@icons/CircleCheckIcon';
import { CircleExclamationMarkIcon } from '@icons/CircleExclamationMarkIcon';

export type NotifyType = {
  type: NOTIFICATION_TYPE;
  message: string | ReactNode;
  duration?: number;
};
interface IUseNotification {
  notify: (val: NotifyType) => void;
  NTFC_TYPE: { [key: string]: NOTIFICATION_TYPE };
}

const StyledNotification = tw.div`flex items-center justify-start w-full py-4 px-5 rounded-lg z-50`;
const StyledText = tw.span`text-[15px] text-inherit`;

const DEFAULT_DURATION = 5000;

const NTFC_TYPE = {
  SUCCESS: 'success' as NOTIFICATION_TYPE,
  WARNING: 'warning' as NOTIFICATION_TYPE,
  DANGER: 'danger' as NOTIFICATION_TYPE,
  INFO: 'info' as NOTIFICATION_TYPE,
};

const NTFC_STYLE = {
  [NTFC_TYPE.SUCCESS]: tw`bg-success-bg text-success-text-and-icon`,
  [NTFC_TYPE.WARNING]: tw`bg-warr-bg text-warr-text-and-icon`,
  [NTFC_TYPE.DANGER]: tw`bg-error-bg text-error-text-icon`,
  [NTFC_TYPE.INFO]: tw`bg-info-bg text-info-text-and-icon`,
};

const NotificationIcon = ({ type }: { type: NOTIFICATION_TYPE }): JSX.Element => {
  switch (type) {
    case NTFC_TYPE.SUCCESS:
      return (
        <CircleCheckIcon
          tw='stroke-success-text-and-icon mr-3 min-w-[16px]'
          width='16'
          height='16'
        />
      );
    case NTFC_TYPE.WARNING:
      return (
        <CircleExclamationMarkIcon
          tw='stroke-warr-text-and-icon mr-3 min-w-[16px]'
          width='16'
          height='16'
        />
      );
    case NTFC_TYPE.DANGER:
      return (
        <CircleExclamationMarkIcon
          tw='stroke-error-text-icon mr-3 min-w-[16px]'
          width='16'
          height='16'
        />
      );
    case NTFC_TYPE.INFO:
      return (
        <CircleCheckIcon tw='stroke-info-text-and-icon mr-3 min-w-[16px]' width='16' height='16' />
      );
    default:
      return (
        <CircleCheckIcon tw='stroke-info-text-and-icon mr-3 min-w-[16px]' width='16' height='16' />
      );
  }
};

export function useNotification(): IUseNotification {
  const notify = ({ type, message, duration = DEFAULT_DURATION }: NotifyType) => {
    Store.addNotification({
      type: type,
      insert: 'bottom',
      container: 'bottom-right',
      dismiss: {
        duration: duration,
      },
      content: (
        <StyledNotification css={[NTFC_STYLE[type]]}>
          <NotificationIcon type={type} />
          <StyledText>{message}</StyledText>
        </StyledNotification>
      ),
      animationIn: ['animate__animated animate__fadeIn'],
      animationOut: ['animate__animated animate__fadeOut'],
      slidingExit: {
        duration: 200,
        timingFunction: 'ease-out',
        delay: 0,
      },
    });
  };

  return { notify, NTFC_TYPE };
}
